/**
 * The fast solver: Knuth's Dancing Links (Algorithm X) over the constraint set.
 *
 * Its job is not to explain anything -- `solver-human.ts` does that. Its job is
 * speed and, above all, *counting*: it keeps searching after the first solution
 * and stops at two, so a candidate puzzle can be classified as unsolvable,
 * non-unique, or uniquely solvable. That classification is what makes the
 * "exactly one solution" promise real, and the generator leans on it once per
 * removed clue.
 *
 * The exact-cover matrix is derived from the ConstraintSet, not from hardcoded
 * rows/columns/boxes:
 *   columns = one per cell ("this cell is filled")
 *           + one per (constraint, digit) ("this digit is placed in this unit")
 *   rows    = one per (cell, digit) placement, covering the cell's column plus
 *             the (constraint, digit) column of every constraint holding it.
 * For classic sudoku that is the familiar 729 x 324 matrix.
 */

import { CELLS, EMPTY, cloneBoard, emptyBoard, type Board, type Digit } from "./grid";
import { CLASSIC, type ConstraintSet } from "./constraints";
import { hasConflict } from "./candidates";

const DIGITS = 9;

interface Dlx {
  readonly left: Int32Array;
  readonly right: Int32Array;
  readonly up: Int32Array;
  readonly down: Int32Array;
  /** node -> its column header index. */
  readonly colOf: Int32Array;
  /** node -> its row id (cell * 9 + digit - 1). */
  readonly rowOf: Int32Array;
  /** column header -> number of live nodes. */
  readonly size: Int32Array;
  /** row id -> index of its first node. */
  readonly rowStart: Int32Array;
  readonly root: number;
  readonly nCols: number;
}

function buildDlx(cs: ConstraintSet): Dlx {
  const nConstraints = cs.constraints.length;
  const nCols = CELLS + nConstraints * DIGITS;
  const root = nCols;

  let nNodes = 0;
  for (let cell = 0; cell < CELLS; cell++) {
    nNodes += DIGITS * (1 + cs.byCell[cell]!.length);
  }

  const total = nCols + 1 + nNodes;
  const left = new Int32Array(total);
  const right = new Int32Array(total);
  const up = new Int32Array(total);
  const down = new Int32Array(total);
  const colOf = new Int32Array(total);
  const rowOf = new Int32Array(total).fill(-1);
  const size = new Int32Array(nCols);
  const rowStart = new Int32Array(CELLS * DIGITS).fill(-1);

  // Header ring, root included.
  for (let c = 0; c < nCols; c++) {
    left[c] = c === 0 ? root : c - 1;
    right[c] = c === nCols - 1 ? root : c + 1;
    up[c] = c;
    down[c] = c;
    colOf[c] = c;
  }
  left[root] = nCols - 1;
  right[root] = 0;
  up[root] = root;
  down[root] = root;

  const unitColumn = (constraintId: number, digit: number): number =>
    CELLS + constraintId * DIGITS + (digit - 1);

  let next = nCols + 1;
  for (let cell = 0; cell < CELLS; cell++) {
    for (let digit = 1; digit <= DIGITS; digit++) {
      const rowId = cell * DIGITS + (digit - 1);
      const columns = [cell, ...cs.byCell[cell]!.map((id) => unitColumn(id, digit))];

      let first = -1;
      let prev = -1;
      for (const column of columns) {
        const node = next++;
        colOf[node] = column;
        rowOf[node] = rowId;

        // splice into the column's vertical ring, above the header
        const last = up[column]!;
        down[last] = node;
        up[node] = last;
        down[node] = column;
        up[column] = node;
        size[column]!++;

        if (first === -1) {
          first = node;
          left[node] = node;
          right[node] = node;
        } else {
          right[prev] = node;
          left[node] = prev;
          right[node] = first;
          left[first] = node;
        }
        prev = node;
      }
      rowStart[rowId] = first;
    }
  }

  return { left, right, up, down, colOf, rowOf, size, rowStart, root, nCols };
}

const dlxCache = new WeakMap<ConstraintSet, Dlx>();

function dlxFor(cs: ConstraintSet): Dlx {
  let dlx = dlxCache.get(cs);
  if (!dlx) {
    dlx = buildDlx(cs);
    dlxCache.set(cs, dlx);
  }
  return dlx;
}

function cover(dlx: Dlx, column: number): void {
  const { left, right, up, down, colOf, size } = dlx;
  right[left[column]!] = right[column]!;
  left[right[column]!] = left[column]!;
  for (let i = down[column]!; i !== column; i = down[i]!) {
    for (let j = right[i]!; j !== i; j = right[j]!) {
      down[up[j]!] = down[j]!;
      up[down[j]!] = up[j]!;
      size[colOf[j]!]!--;
    }
  }
}

function uncover(dlx: Dlx, column: number): void {
  const { left, right, up, down, colOf, size } = dlx;
  for (let i = up[column]!; i !== column; i = up[i]!) {
    for (let j = left[i]!; j !== i; j = left[j]!) {
      size[colOf[j]!]!++;
      down[up[j]!] = j;
      up[down[j]!] = j;
    }
  }
  right[left[column]!] = column;
  left[right[column]!] = column;
}

/** Cover the rows fixed by the board's givens. Returns them for undo. */
function applyGivens(dlx: Dlx, board: Board): number[] {
  const covered: number[] = [];
  for (let cell = 0; cell < CELLS; cell++) {
    const digit = board[cell]!;
    if (digit === EMPTY) continue;
    const first = dlx.rowStart[cell * DIGITS + (digit - 1)]!;
    cover(dlx, dlx.colOf[first]!);
    covered.push(dlx.colOf[first]!);
    for (let j = dlx.right[first]!; j !== first; j = dlx.right[j]!) {
      cover(dlx, dlx.colOf[j]!);
      covered.push(dlx.colOf[j]!);
    }
  }
  return covered;
}

function undoGivens(dlx: Dlx, covered: number[]): void {
  for (let i = covered.length - 1; i >= 0; i--) uncover(dlx, covered[i]!);
}

/** Smallest live column -- Knuth's S heuristic, and the reason this is fast. */
function chooseColumn(dlx: Dlx): number {
  const { right, size, root } = dlx;
  let best = -1;
  let bestSize = Infinity;
  for (let c = right[root]!; c !== root; c = right[c]!) {
    const s = size[c]!;
    if (s < bestSize) {
      bestSize = s;
      best = c;
      if (s <= 1) break;
    }
  }
  return best;
}

function search(dlx: Dlx, stack: number[], limit: number, found: Board[]): number {
  if (dlx.right[dlx.root]! === dlx.root) {
    const board = emptyBoard();
    for (const rowId of stack) {
      board[(rowId / DIGITS) | 0] = ((rowId % DIGITS) + 1) as Digit;
    }
    found.push(board);
    return found.length;
  }

  const column = chooseColumn(dlx);
  if (column === -1 || dlx.size[column] === 0) return found.length;

  cover(dlx, column);
  for (let i = dlx.down[column]!; i !== column; i = dlx.down[i]!) {
    stack.push(dlx.rowOf[i]!);
    for (let j = dlx.right[i]!; j !== i; j = dlx.right[j]!) cover(dlx, dlx.colOf[j]!);

    search(dlx, stack, limit, found);

    for (let j = dlx.left[i]!; j !== i; j = dlx.left[j]!) uncover(dlx, dlx.colOf[j]!);
    stack.pop();

    if (found.length >= limit) break;
  }
  uncover(dlx, column);

  return found.length;
}

function solveUpTo(board: Board, limit: number, cs: ConstraintSet): Board[] {
  if (hasConflict(board, cs)) return [];
  const dlx = dlxFor(cs);
  const covered = applyGivens(dlx, board);
  const found: Board[] = [];
  try {
    search(dlx, [], limit, found);
  } finally {
    undoGivens(dlx, covered);
  }
  // Solutions carry only the search's own placements; fold the givens back in.
  for (const solution of found) {
    for (let cell = 0; cell < CELLS; cell++) {
      if (board[cell] !== EMPTY) solution[cell] = board[cell]!;
    }
  }
  return found;
}

/**
 * How many solutions the board has, counting no further than `limit`.
 *
 * The default limit of 2 is the one that matters: 0 unsolvable, 1 uniquely
 * solvable, 2 "at least two" -- which is all the generator ever needs to know.
 */
export function countSolutions(board: Board, limit = 2, cs: ConstraintSet = CLASSIC): number {
  return solveUpTo(board, limit, cs).length;
}

/** The first solution found, or null if there is none. */
export function solve(board: Board, cs: ConstraintSet = CLASSIC): Board | null {
  const found = solveUpTo(board, 1, cs);
  return found.length > 0 ? found[0]! : null;
}

/** The unique solution, or null if the puzzle has zero or several. */
export function solveUnique(board: Board, cs: ConstraintSet = CLASSIC): Board | null {
  const found = solveUpTo(board, 2, cs);
  return found.length === 1 ? cloneBoard(found[0]!) : null;
}

export function hasUniqueSolution(board: Board, cs: ConstraintSet = CLASSIC): boolean {
  return countSolutions(board, 2, cs) === 1;
}
